import React, { useEffect, useMemo, useState, FormEvent } from "react";
import { ensureFonts } from "./fonts";
import { renderSpecPreview } from "./fabricKit";
import { Icon } from "./Icon";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import {
  createStudioDesign,
  deleteStudioDesign,
  duplicateStudioDesign,
  fetchStudioDesigns,
  fetchStudioFormats,
  fetchStudioTemplates,
  fetchStudioVideoJobs,
} from "../../features/studio-controller/StudioServices";
import {
  StudioDesign,
  StudioFormat,
  StudioTemplate,
  StudioVideoJob,
} from "../../types/studioTypes";

/* ------------------------------------------------------------ Interfaces & Types */

export interface StudioHomeProps {
  onOpenDesign: (id: number) => void;
  onNewVideo: () => void;
}

export interface StudioState {
  formats: StudioFormat[];
  templates: StudioTemplate[] | null;
  designs: StudioDesign[] | null;
  jobs: StudioVideoJob[] | null;
  designsError: string | null;
}

export interface RootState {
  studio: StudioState;
}

interface CustomSize {
  w: number;
  h: number;
  unit: SizeUnit;
  dpi: number;
}

interface CreateDesignPayload {
  formatKey?: string;
  templateId?: number;
  width?: number;
  height?: number;
}

const CATEGORIES: [string, string][] = [
  ["", "All"],
  ["OFFER", "Offers"],
  ["FESTIVAL", "Festivals"],
  ["STATUS", "Status"],
  ["PROFILE", "Profile photo"],
  ["ANNOUNCEMENT", "Announcements"],
  ["GREETING", "Greetings"],
];

/* ------------------------------------------------------------ Shared Class Strings */
const link =
  "inline-flex items-center gap-1.5 bg-transparent border-0 py-1 px-0 text-st-green-dark font-semibold text-sm cursor-pointer";

const btnAccentSm =
  "inline-flex items-center justify-center gap-2 min-h-[34px] px-[11px] py-[7px] rounded-[9px] border border-st-green bg-st-green text-white font-semibold text-[13px] leading-none whitespace-nowrap cursor-pointer hover:enabled:bg-st-green-dark hover:enabled:border-st-green-dark disabled:opacity-50 disabled:cursor-not-allowed";

const iconBtnSm =
  "w-[30px] h-[30px] inline-grid place-items-center flex-none rounded-lg border-0 bg-transparent text-st-muted cursor-pointer hover:enabled:bg-st-paper hover:enabled:text-st-ink disabled:opacity-[.35] disabled:cursor-not-allowed";

const iconBtnSmDanger =
  "w-[30px] h-[30px] inline-grid place-items-center flex-none rounded-lg border-0 bg-transparent text-st-red cursor-pointer hover:enabled:bg-[#fbe9e5] hover:enabled:text-st-red disabled:opacity-[.35] disabled:cursor-not-allowed";

const skelBase =
  "block bg-[linear-gradient(90deg,#edf2ef_0%,#f6f9f7_50%,#edf2ef_100%)] bg-[length:200%_100%] animate-st-shimmer";
const skelCard = `${skelBase} rounded-[10px] aspect-square`;
const skelFill = `${skelBase} absolute inset-0`;

const h2 = "font-display font-bold text-[21px] tracking-[-0.01em]";
const hint = "mt-2 text-[13px] leading-normal text-st-muted";
const empty =
  "m-0 p-[22px] border border-dashed border-st-line rounded-st text-st-muted text-sm text-center";

const sizeCardCommon =
  "relative flex flex-col gap-[3px] p-3.5 min-h-[150px] rounded-st border border-st-line bg-white text-left text-st-ink transition-[border-color,box-shadow] duration-150";
const sizeCardButton = `${sizeCardCommon} items-start cursor-pointer hover:enabled:border-st-green hover:enabled:shadow-[0_8px_22px_-14px_rgba(11,122,90,0.6)]`;
const sizeCardCustom = `${sizeCardCommon} justify-center items-stretch border-dashed cursor-default`;

const chipBase =
  "border rounded-full px-[13px] py-[7px] text-[13px] font-medium cursor-pointer";

const startCard =
  "flex items-center gap-3.5 text-left py-4 px-[18px] rounded-2xl cursor-pointer bg-white/[0.08] border border-white/[0.16] text-white transition-[background-color,transform] duration-150 hover:bg-white/[0.14] hover:-translate-y-px";
const startIcon =
  "w-12 h-12 rounded-[14px] grid place-items-center flex-none text-white";

const STATUS_STYLES: Record<string, string> = {
  DONE: "bg-st-green-soft text-st-green-dark",
  RUNNING: "bg-[#fdf3dc] text-[#7a5410]",
  QUEUED: "bg-[#fdf3dc] text-[#7a5410]",
  FAILED: "bg-[#fbe9e5] text-st-red",
};

// Thunks reject with a plain string, so .unwrap() throws a string, not an Error.
const errMessage = (e: unknown): string => {
  if (typeof e === "string") return e;
  if (
    e &&
    typeof e === "object" &&
    "message" in e &&
    typeof (e as Error).message === "string"
  ) {
    return (e as Error).message;
  }
  return "Something went wrong";
};

type SizeUnit = "px" | "in" | "cm" | "mm";

const DEFAULT_DPI = 300;

function toPx(value: number, unit: SizeUnit, dpi: number): number {
  if (!Number.isFinite(value)) return 0;
  if (unit === "px") return Math.round(value);
  const inches =
    unit === "in" ? value : unit === "cm" ? value / 2.54 : value / 25.4;
  return Math.round(inches * dpi);
}

function fromPx(px: number, unit: SizeUnit, dpi: number): string {
  if (unit === "px") return String(Math.round(px));
  const inches = px / dpi;
  const value =
    unit === "in" ? inches : unit === "cm" ? inches * 2.54 : inches * 25.4;
  return (Math.round(value * 100) / 100).toString();
}

const UNIT_LABEL: Record<SizeUnit, string> = {
  px: "px",
  in: "in",
  cm: "cm",
  mm: "mm",
};

/** Studio landing page: start a design from a size or template, reopen designs, start a video. */
export default function StudioHome({
  onOpenDesign,
  onNewVideo,
}: StudioHomeProps) {
  const [previews, setPreviews] = useState<Record<number, string>>({});
  const [category, setCategory] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [custom, setCustom] = useState<CustomSize | null>(null);

  // Type-safe dispatch for async thunks
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<ThunkDispatch<RootState, any, any>>();

  const { formats, templates, designs, jobs, designsError } = useSelector(
    (state: RootState) => state.studio,
  );

  useEffect(() => {
    dispatch(fetchStudioFormats());
    dispatch(fetchStudioTemplates());
    dispatch(fetchStudioDesigns({ page: 0, size: 24 }));
    dispatch(fetchStudioVideoJobs());
  }, [dispatch]);

  useEffect(() => {
    if (designsError) setError(designsError);
  }, [designsError]);

  function loadDesigns() {
    dispatch(fetchStudioDesigns({ page: 0, size: 24 }));
  }

  // Template cards are drawn in the browser from their layout, once the fonts are in.
  useEffect(() => {
    if (!templates?.length) return;
    let stop = false;
    (async () => {
      await ensureFonts();
      for (const t of templates) {
        if (stop) return;
        if (t.thumbnailUrl) continue;
        try {
          const url = await renderSpecPreview(
            JSON.parse(t.specJson),
            t.width,
            t.height,
          );
          if (!stop) setPreviews((p) => ({ ...p, [t.id]: url }));
        } catch {
          /* a broken layout just shows no preview */
        }
      }
    })();
    return () => {
      stop = true;
    };
  }, [templates]);

  const shownTemplates = useMemo(
    () => (templates || []).filter((t) => !category || t.category === category),
    [templates, category],
  );

  async function create(body: CreateDesignPayload, key: string) {
    setBusy(key);
    setError(null);
    try {
      const d = await dispatch(createStudioDesign(body)).unwrap();
      onOpenDesign(d.id);
    } catch (e) {
      setError(errMessage(e));
      setBusy(null);
    }
  }

  async function duplicate(d: StudioDesign) {
    setBusy(`dup-${d.id}`);
    try {
      await dispatch(duplicateStudioDesign(d.id)).unwrap();
      loadDesigns();
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setBusy(null);
    }
  }

  async function remove(d: StudioDesign) {
    if (
      !window.confirm(
        `Delete "${d.name}"? Images already saved to your Gallery stay there.`,
      )
    )
      return;
    setBusy(`del-${d.id}`);
    try {
      await dispatch(deleteStudioDesign(d.id)).unwrap();
      loadDesigns();
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="max-w-[1240px] mx-auto pt-2 pb-16">
      <section className="relative overflow-hidden grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] gap-7 items-center p-9 rounded-[22px] text-white bg-[radial-gradient(120%_140%_at_0%_0%,#0288D1_0%,#014B73_45%,#0E1C2B_100%)] after:content-[''] after:absolute after:-right-20 after:-top-[120px] after:w-[360px] after:h-[360px] after:rounded-full after:bg-[radial-gradient(circle,rgba(2,136,209,0.35),transparent_70%)] after:pointer-events-none max-[820px]:grid-cols-1 max-[820px]:p-6">
        <div>
          <p className="mb-2 text-xs font-bold tracking-[0.14em] uppercase text-[#8fe0b5]">
            Empedance Studio
          </p>
          <h1 className="m-0 font-display font-bold text-[clamp(28px,4vw,44px)] leading-[1.05] tracking-[-0.02em]">
            What will you make today?
          </h1>
          <p className="mt-3 text-base leading-[1.55] text-[#cfe3da] max-w-[52ch]">
            Design template headers, offers, Status posts and your profile
            photo, or edit a video with music. Everything saves to your Gallery,
            ready to send.
          </p>
        </div>
        <div className="relative z-[1] grid gap-3">
          <button
            className={startCard}
            onClick={() =>
              document
                .getElementById("st-sizes")
                ?.scrollIntoView({ behavior: "smooth" })
            }
          >
            <span
              className={`${startIcon} bg-[linear-gradient(135deg,#7c3aed,#db2777)]`}
            >
              <Icon name="shapes" size={26} />
            </span>
            <span>
              <strong className="block text-base">Create a design</strong>
              <small className="block text-[13px] text-[#b9d3c8] mt-0.5">
                Images, headers, posters
              </small>
            </span>
          </button>
          <button className={startCard} onClick={onNewVideo}>
            <span
              className={`${startIcon} bg-[linear-gradient(135deg,#f97316,#e11d48)]`}
            >
              <Icon name="video" size={26} />
            </span>
            <span>
              <strong className="block text-base">Edit a video</strong>
              <small className="block text-[13px] text-[#b9d3c8] mt-0.5">
                Trim, text, music
              </small>
            </span>
          </button>
        </div>
      </section>

      {error && (
        <div
          className="flex items-center justify-between gap-3 mb-4 py-2.5 pr-2.5 pl-3.5 rounded-[10px] bg-[#fbe9e5] text-[#8f2c1d] text-sm"
          role="alert"
        >
          {error}
          <button
            className={iconBtnSm}
            onClick={() => setError(null)}
            aria-label="Dismiss"
          >
            <Icon name="close" size={16} />
          </button>
        </div>
      )}

      <section className="mt-9" id="st-sizes">
        <h2 className={`${h2} mb-3.5`}>Start with a size</h2>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(168px,1fr))] gap-3">
          {(formats || []).map((f) => (
            <button
              key={f.key}
              className={sizeCardButton}
              disabled={!!busy}
              onClick={() => create({ formatKey: f.key }, f.key)}
            >
              <span className="h-[52px] flex items-center mb-1.5">
                <span
                  className="block rounded-[4px] bg-[linear-gradient(135deg,#dcefe7,#b9e2cf)] border-[1.5px] border-st-green"
                  style={sizeShape(f.width, f.height)}
                />
              </span>
              <strong className="text-sm">{f.label}</strong>
              <small className="text-xs text-st-muted">
                {f.width} × {f.height}
              </small>
              <small className="text-xs text-st-muted leading-[1.35]">
                {f.use}
              </small>
              {busy === f.key && (
                <span className="absolute inset-0 grid place-items-center bg-white/85 rounded-[inherit] font-semibold text-[13px]">
                  Opening…
                </span>
              )}
            </button>
          ))}
          <div
            className={`${sizeCardCustom}${custom ? " col-span-2 sm:col-span-1 md:col-span-2" : ""}`}
          >
            {!custom ? (
              <button
                className={`${link} justify-center h-full`}
                onClick={() =>
                  setCustom({ w: 1080, h: 1080, unit: "px", dpi: DEFAULT_DPI })
                }
              >
                <Icon name="plus" size={18} /> Custom size
              </button>
            ) : (
              <form
                onSubmit={(e: FormEvent<HTMLFormElement>) => {
                  e.preventDefault();
                  create(
                    {
                      formatKey: "CUSTOM",
                      width: toPx(custom.w, custom.unit, custom.dpi),
                      height: toPx(custom.h, custom.unit, custom.dpi),
                    },
                    "custom",
                  );
                }}
              >
                <div className="w-full min-w-0 flex items-end gap-1.5 mb-2">
                  <label className="grid gap-1 text-xs font-semibold text-st-muted min-w-0 flex-1">
                    Width
                    <input
                      className="w-full min-w-0 text-sm px-2 py-1.5 border border-st-line rounded-lg"
                      type="number"
                      step={custom.unit === "px" ? 1 : 0.01}
                      min={custom.unit === "px" ? 100 : 0.1}
                      value={custom.w}
                      onChange={(e) =>
                        setCustom({ ...custom, w: Number(e.target.value) })
                      }
                    />
                  </label>
                  <span
                    className="pb-2 text-st-muted flex-none"
                    aria-hidden="true"
                  >
                    ×
                  </span>
                  <label className="grid gap-1 text-xs font-semibold text-st-muted min-w-0 flex-1">
                    Height
                    <input
                      className="w-full min-w-0 text-sm px-2 py-1.5 border border-st-line rounded-lg"
                      type="number"
                      step={custom.unit === "px" ? 1 : 0.01}
                      min={custom.unit === "px" ? 100 : 0.1}
                      value={custom.h}
                      onChange={(e) =>
                        setCustom({ ...custom, h: Number(e.target.value) })
                      }
                    />
                  </label>
                </div>

                <label className="grid gap-1 text-xs font-semibold text-st-muted mb-2 w-full min-w-0">
                  Unit
                  <select
                    className="w-full min-w-0 text-sm px-2 py-1.5 border border-st-line rounded-lg cursor-pointer"
                    value={custom.unit}
                    onChange={(e) => {
                      const nextUnit = e.target.value as SizeUnit;
                      if (nextUnit === custom.unit) return;
                      const curPxW = toPx(custom.w, custom.unit, custom.dpi);
                      const curPxH = toPx(custom.h, custom.unit, custom.dpi);
                      setCustom({
                        ...custom,
                        unit: nextUnit,
                        w: Number(fromPx(curPxW, nextUnit, custom.dpi)),
                        h: Number(fromPx(curPxH, nextUnit, custom.dpi)),
                      });
                    }}
                  >
                    {(["px", "in", "cm", "mm"] as SizeUnit[]).map((u) => (
                      <option key={u} value={u}>
                        {UNIT_LABEL[u]}
                      </option>
                    ))}
                  </select>
                </label>

                {custom.unit !== "px" && (
                  <div className="flex flex-wrap items-center gap-1.5 mb-2.5 text-xs text-st-muted min-w-0">
                    <label className="flex items-center gap-1">
                      DPI
                      <input
                        className="w-14 text-xs px-1.5 py-1 border border-st-line rounded-md"
                        type="number"
                        min={1}
                        value={custom.dpi}
                        onChange={(e) =>
                          setCustom({
                            ...custom,
                            dpi: Math.max(
                              1,
                              Number(e.target.value) || DEFAULT_DPI,
                            ),
                          })
                        }
                      />
                    </label>
                    <span>
                      ≈ {toPx(custom.w, custom.unit, custom.dpi)} ×{" "}
                      {toPx(custom.h, custom.unit, custom.dpi)} px
                    </span>
                  </div>
                )}

                <button className={btnAccentSm} disabled={!!busy}>
                  {busy === "custom" ? "Opening…" : "Create"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <section className="mt-9">
        <div className="flex items-center justify-between gap-x-5 gap-y-3 flex-wrap mb-3.5">
          <h2 className={`${h2} m-0`}>Templates</h2>
          <div
            className="flex flex-wrap gap-1.5"
            role="tablist"
            aria-label="Template category"
          >
            {CATEGORIES.map(([key, label]) => (
              <button
                key={key || "all"}
                role="tab"
                aria-selected={category === key}
                className={`${chipBase} ${
                  category === key
                    ? "bg-st-ink border-st-ink text-white"
                    : "bg-white border-st-line text-st-ink"
                }`}
                onClick={() => setCategory(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {templates === null && (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(190px,1fr))] gap-[18px] items-start">
            {Array.from({ length: 8 }).map((_, i) => (
              <span key={i} className={skelCard} />
            ))}
          </div>
        )}
        {templates && shownTemplates.length === 0 && (
          <p className={hint}>No templates in this category yet.</p>
        )}
        <div className="grid grid-cols-[repeat(auto-fill,minmax(190px,1fr))] gap-[18px] items-start">
          {shownTemplates.map((t) => (
            <button
              key={t.id}
              className="group flex flex-col gap-[3px] p-0 border-0 bg-transparent text-left cursor-pointer text-st-ink"
              disabled={!!busy}
              onClick={() => create({ templateId: t.id }, `t-${t.id}`)}
            >
              <span
                className={`relative block w-full max-h-[300px] rounded-xl overflow-hidden bg-st-paper shadow-[0_0_0_1px_rgba(16,35,30,0.08)] mb-1.5 transition-[box-shadow,transform] duration-150 group-hover:shadow-[0_0_0_2px_#0b7a5a,0_14px_30px_-18px_rgba(0,0,0,0.5)] group-hover:-translate-y-0.5 group-focus-visible:shadow-[0_0_0_2px_#0b7a5a,0_14px_30px_-18px_rgba(0,0,0,0.5)] group-focus-visible:-translate-y-0.5`}
                style={{ aspectRatio: `${t.width} / ${t.height}` }}
              >
                {t.thumbnailUrl || previews[t.id] ? (
                  <img
                    className="w-full h-full object-cover block"
                    src={t.thumbnailUrl || previews[t.id]}
                    alt=""
                  />
                ) : (
                  <span className={skelFill} />
                )}
                <span className="absolute inset-x-2.5 bottom-2.5 p-2 rounded-[9px] text-center bg-[rgba(14,28,24,0.86)] text-white text-[13px] font-semibold opacity-0 translate-y-1.5 transition-all duration-150 group-hover:opacity-100 group-hover:translate-y-0 group-focus-visible:opacity-100 group-focus-visible:translate-y-0">
                  {busy === `t-${t.id}` ? "Opening…" : "Use template"}
                </span>
              </span>
              <strong className="text-sm">{t.name}</strong>
              <small className="text-xs text-st-muted">
                {formatLabel(formats, t.formatKey)}
              </small>
            </button>
          ))}
        </div>
      </section>

      <section className="mt-9">
        <h2 className={`${h2} mb-3.5`}>Your designs</h2>
        {designs === null && (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(190px,1fr))] gap-[18px] items-start">
            {Array.from({ length: 4 }).map((_, i) => (
              <span key={i} className={skelCard} />
            ))}
          </div>
        )}
        {designs && designs.length === 0 && (
          <p className={empty}>
            Designs you make appear here. Pick a size or template above to
            start.
          </p>
        )}
        <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-[18px]">
          {(designs || []).map((d) => (
            <article
              key={d.id}
              className="rounded-st border border-st-line bg-white overflow-hidden"
            >
              <button
                className="grid place-items-center w-full h-[170px] p-3 border-0 bg-st-paper cursor-pointer"
                onClick={() => onOpenDesign(d.id)}
                aria-label={`Open ${d.name}`}
              >
                {d.thumbnailUrl ? (
                  <img
                    className="max-w-full max-h-[150px] object-contain rounded-[4px] shadow-[0_4px_14px_-6px_rgba(0,0,0,0.3)]"
                    src={d.thumbnailUrl}
                    alt=""
                  />
                ) : (
                  <span
                    className="block max-h-full w-[70%] bg-white border border-st-line"
                    style={{ aspectRatio: `${d.width} / ${d.height}` }}
                  />
                )}
              </button>
              <div className="flex justify-between items-center gap-2 py-2.5 pr-2 pl-3">
                <div className="min-w-0">
                  <strong className="block text-sm truncate" title={d.name}>
                    {d.name}
                  </strong>
                  <small className="block text-xs text-st-muted">
                    {d.width} × {d.height}
                    {d.galleryId ? " · In Gallery" : ""}
                  </small>
                </div>
                <div className="flex">
                  <button
                    className={iconBtnSm}
                    onClick={() => duplicate(d)}
                    disabled={!!busy}
                    aria-label={`Duplicate ${d.name}`}
                    title="Duplicate"
                  >
                    <Icon name="copy" size={16} />
                  </button>
                  <button
                    className={iconBtnSmDanger}
                    onClick={() => remove(d)}
                    disabled={!!busy}
                    aria-label={`Delete ${d.name}`}
                    title="Delete"
                  >
                    <Icon name="trash" size={16} />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="mt-9">
        <div className="flex items-center justify-between gap-x-5 gap-y-3 flex-wrap mb-3.5">
          <h2 className={`${h2} m-0`}>Your videos</h2>
          <button className={btnAccentSm} onClick={onNewVideo}>
            <Icon name="plus" size={16} /> New video
          </button>
        </div>
        {jobs && jobs.length === 0 && (
          <p className={empty}>
            Edited videos appear here and in your Gallery.
          </p>
        )}
        <div className="grid gap-2">
          {(jobs || []).map((j) => (
            <div
              key={j.id}
              className="flex items-center gap-3.5 py-2.5 px-3.5 border border-st-line rounded-xl bg-white text-sm flex-wrap"
            >
              <span
                className={`font-semibold text-[13px] px-2.5 py-1 rounded-full ${
                  STATUS_STYLES[j.status] || "bg-st-paper"
                }`}
              >
                {statusLabel(j)}
              </span>
              <span className="text-st-muted text-[13px]">
                {j.createdAt ? new Date(j.createdAt).toLocaleString() : ""}
              </span>
              {j.status === "DONE" && j.outputUrl && (
                <a
                  className={link}
                  href={j.outputUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Watch
                </a>
              )}
              {j.status === "FAILED" && (
                <span className="text-st-red text-[13px] mt-2">{j.error}</span>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

/* ------------------------------------------------------------ Helpers */

function statusLabel(j: StudioVideoJob): string {
  if (j.status === "DONE")
    return `Ready${j.outputBytes ? ` · ${(j.outputBytes / 1048576).toFixed(1)} MB` : ""}`;
  if (j.status === "RUNNING") return `Rendering ${j.progress ?? 0}%`;
  if (j.status === "QUEUED") return "Waiting";
  return "Failed";
}

function formatLabel(formats: StudioFormat[], key: string): string {
  return formats.find((f) => f.key === key)?.label || key;
}

function sizeShape(w: number, h: number): React.CSSProperties {
  const s = 46 / Math.max(w, h);
  return { width: Math.max(10, w * s), height: Math.max(10, h * s) };
}
